import React, { useState, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { WechatLogo } from "@phosphor-icons/react/dist/csr/WechatLogo";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import TextEditor from "../../../TextEditor/TextEditor";
import CustomHtml from "../../../common/general/CustomHtml/CustomHtml";
import { useGetLastSeenPostQuery, useUpdateLastSeenPostMutation } from "../../../../graphql/generated/graphql";

interface Author {
  id: string;
  name: string;
}

interface Reply {
  id: string;
  message: string;
  depth: number;
  created: string;
  author: Author;
}

interface Post {
  id: string;
  message: string;
  depth: number;
  threadId: string;
  created: string;
  lastModified: string;
  author: Author;
  replies: Reply[];
}

interface ResponseCardProps {
  post: Post;
  onReply: (postId: string, message: string) => void;
  currentUser: {
    id: string;
    name: string;
  };
  onPostViewed?: (postId: string) => void;
}

const ResponseCard: React.FC<ResponseCardProps> = ({ post, onReply, currentUser, onPostViewed }) => {
  const [isRepliesExpanded, setIsRepliesExpanded] = useState(false);
  const [isReplyEditorOpen, setIsReplyEditorOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewPost, setIsNewPost] = useState(false);
  const [newReplies, setNewReplies] = useState<Set<string>>(new Set());
  const [hasReportedAsViewed, setHasReportedAsViewed] = useState(false);

  const [updateLastSeenPostMutation] = useUpdateLastSeenPostMutation();

  const { data: lastSeenData, loading: lastSeenLoading } = useGetLastSeenPostQuery({
    variables: { threadId: post.threadId },
    skip: !post.threadId,
  });

  useEffect(() => {
    if (!lastSeenLoading && lastSeenData?.getLastSeenPost?.data) {
      const lastSeenAt = lastSeenData.getLastSeenPost.data.lastSeenAt;

      if (!lastSeenAt) {
        // User has never viewed this thread, mark all as new
        setIsNewPost(true);
        setNewReplies(new Set(post.replies.map((reply) => reply.id)));
        return;
      }

      const lastSeenTime = new Date(lastSeenAt).getTime();
      const postTime = new Date(post.created).getTime();

      // Check if this post is newer than last seen
      const postIsNew = postTime > lastSeenTime;
      setIsNewPost(postIsNew);

      // Check which replies are new
      const newReplyIds = new Set(
        post.replies
          .filter((reply) => {
            const replyTime = new Date(reply.created).getTime();
            const isNew = replyTime > lastSeenTime;
            return isNew;
          })
          .map((reply) => reply.id)
      );
      setNewReplies(newReplyIds);
    } else if (!lastSeenLoading && !lastSeenData?.getLastSeenPost?.data) {
      setIsNewPost(true);
      setNewReplies(new Set(post.replies.map((reply) => reply.id)));
    }
  }, [lastSeenData, lastSeenLoading, post]);

  // Report to parent when this post is viewed (but don't auto-mark as seen)
  useEffect(() => {
    if (isNewPost && !hasReportedAsViewed && onPostViewed) {
      // Small delay to ensure user actually sees the post
      const timer = setTimeout(() => {
        onPostViewed(post.id);
        setHasReportedAsViewed(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isNewPost, hasReportedAsViewed, onPostViewed, post.id]);

  const getStudentInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return "just now";
    } else if (diffInHours < 24) {
      return `about ${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onReply(post.id, replyContent);
      setReplyContent("");
      setIsReplyEditorOpen(false);
    } catch (error) {
      console.error("Error submitting reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReplyContent("");
    setIsReplyEditorOpen(false);
  };

  const handleToggleReplies = async () => {
    setIsRepliesExpanded(!isRepliesExpanded);

    if (!isRepliesExpanded && post.replies.length > 0 && newReplies.size > 0) {
      const sortedReplies = [...post.replies].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
      const mostRecentReply = sortedReplies[0];

      if (mostRecentReply && onPostViewed) {
        onPostViewed(mostRecentReply.id);
        setNewReplies(new Set());
      }
    }
  };

  const renderReplyAvatars = () => {
    const maxVisible = 3;
    const visibleReplies = post.replies.slice(0, maxVisible);
    const remainingCount = Math.max(0, post.replies.length - maxVisible);

    return (
      <View style={styles.replyAvatarsContainer}>
        {visibleReplies.map((reply, index) => (
          <View
            key={reply.id}
            style={[styles.replyAvatar, { marginLeft: index > 0 ? -8 : 0, zIndex: maxVisible - index }]}>
            <CustomText
              text={getStudentInitial(reply.author.name)}
              size="xs"
              weight="bold"
              style={styles.replyAvatarText}
              useTranslationText={false}
            />
          </View>
        ))}
        {remainingCount > 0 && (
          <View style={[styles.replyAvatar, { marginLeft: -8, backgroundColor: "#E5E7EB" }]}>
            <CustomText
              text={`+${remainingCount}`}
              size="xs"
              weight="bold"
              style={[styles.replyAvatarText, { color: "#6B7280" }]}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
    );
  };

  const renderExpandedReplies = () => {
    return (
      <ScrollView style={styles.expandedRepliesScrollContainer} showsVerticalScrollIndicator={true}>
        <View style={styles.expandedRepliesContainer}>
          {post.replies.map((reply) => {
            const isNewReply = newReplies.has(reply.id);
            const wasJustViewed = !isNewReply && lastSeenData?.getLastSeenPost?.data?.lastSeenPostId === reply.id;

            return (
              <View key={reply.id} style={styles.replyCard}>
                <View style={styles.replyHeader}>
                  <View style={styles.replyAvatar}>
                    <CustomText
                      text={getStudentInitial(reply.author.name)}
                      size="xs"
                      weight="bold"
                      style={styles.replyAvatarText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.replyInfo}>
                    <View style={styles.replyAuthorRow}>
                      {isNewReply && (
                        <View style={styles.newBadgeSmall}>
                          <CustomText
                            text="New"
                            size="xs"
                            weight="ultraBold"
                            style={styles.newBadgeTextSmall}
                            useTranslationText={false}
                          />
                        </View>
                      )}
                      <CustomText
                        text={reply.author.name}
                        size="s"
                        weight="bold"
                        style={[styles.replyAuthorName, isNewReply && styles.replyAuthorNameWithBadge]}
                        useTranslationText={false}
                      />
                    </View>
                    <CustomText
                      text={formatTimeAgo(reply.created)}
                      size="xs"
                      style={styles.replyTimestamp}
                      useTranslationText={false}
                    />
                  </View>
                </View>
                <CustomSpacing size="s" type="vertical" />
                <View style={styles.replyMessageContainer}>
                  <CustomHtml html={reply.message} style={styles.replyMessage} />
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>
    );
  };

  return (
    <View style={styles.container}>
      {/* Main Response Card */}
      <View style={styles.responseCard}>
        {/* Header with Avatar, Name, and Timestamp */}
        <View style={styles.header}>
          <View style={styles.authorAvatar}>
            <CustomText
              text={getStudentInitial(post.author.name)}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.headerInfo}>
            <View style={styles.authorRow}>
              {isNewPost && (
                <View style={styles.newBadge}>
                  <CustomText
                    text="New"
                    size="xs"
                    weight="ultraBold"
                    style={styles.newBadgeText}
                    useTranslationText={false}
                  />
                </View>
              )}
              <CustomText
                text={post.author.name}
                size="m"
                weight="bold"
                style={[styles.authorName, isNewPost && styles.authorNameWithBadge]}
                useTranslationText={false}
              />
            </View>
            <CustomText
              text={formatTimeAgo(post.created)}
              size="s"
              style={styles.timestamp}
              useTranslationText={false}
            />
          </View>
        </View>

        <CustomSpacing size="xl" type="vertical" />

        {/* Message Content */}
        <View style={styles.messageContainer}>
          <CustomHtml html={post.message} style={styles.message} />
        </View>

        <CustomSpacing size="xl" type="vertical" />

        {/* Reply Button */}
        <View style={styles.actionContainer}>
          <CustomButton
            text="Reply"
            onPress={() => setIsReplyEditorOpen(true)}
            styleType="primaryLight"
            bold={true}
            style={{ borderWidth: 1, borderColor: "#03040F19", paddingHorizontal: 12, paddingVertical: 8 }}
          />
        </View>

        {/* Reply Editor */}
        {isReplyEditorOpen && (
          <>
            <CustomSpacing size="m" type="vertical" />
            <View style={styles.replyEditorContainer}>
              <View style={styles.replyEditorHeader}>
                <View style={styles.currentUserAvatar}>
                  <CustomText
                    text={getStudentInitial(currentUser.name)}
                    size="s"
                    weight="bold"
                    style={styles.avatarText}
                    useTranslationText={false}
                  />
                </View>
                <CustomText
                  text={`Your reply to ${post.author.name}`}
                  size="s"
                  weight="bold"
                  style={styles.replyToText}
                  useTranslationText={false}
                />
              </View>

              <CustomSpacing size="m" type="vertical" />

              <View style={styles.editorContainer}>
                <TextEditor
                  value={replyContent}
                  onChange={setReplyContent}
                  placeholder="Write your reply..."
                  disabled={isSubmitting}
                />
              </View>

              <CustomSpacing size="m" type="vertical" />

              <View style={styles.replyButtonsContainer}>
                <CustomButton
                  text="Reply"
                  onPress={handleReply}
                  styleType="primary"
                  style={{ paddingHorizontal: 12, paddingVertical: 8 }}
                  bold={true}
                  disabled={!replyContent.trim() || isSubmitting}
                />
                <CustomSpacing size="s" type="horizontal" />
                <CustomButton
                  text="Cancel"
                  onPress={handleCancel}
                  styleType="primaryLight"
                  bold={true}
                  style={{ borderWidth: 1, borderColor: "#03040F19", paddingHorizontal: 12, paddingVertical: 8 }}
                />
              </View>
            </View>
          </>
        )}

        {/* Reply Count and Avatars Section */}
        {post.replies.length > 0 && (
          <>
            <CustomSpacing size="m" type="vertical" />
            <TouchableOpacity onPress={handleToggleReplies} activeOpacity={0.7}>
              <View style={styles.repliesSection}>
                <View style={styles.repliesRow}>
                  <WechatLogo size={32} color="#344054" />
                  <CustomSpacing size="xs" type="horizontal" />
                  <View style={styles.repliesCountContainer}>
                    {newReplies.size > 0 && (
                      <View style={styles.newBadgeSmall}>
                        <CustomText
                          text="New"
                          size="xs"
                          weight="ultraBold"
                          style={styles.newBadgeTextSmall}
                          useTranslationText={false}
                        />
                      </View>
                    )}
                    <CustomText
                      text={`${post.replies.length} ${post.replies.length === 1 ? "reply" : "replies"}`}
                      size="s"
                      style={[styles.repliesCount, newReplies.size > 0 && styles.repliesCountWithBadge]}
                      useTranslationText={false}
                      weight="bold"
                    />
                  </View>
                  <CustomSpacing size="m" type="horizontal" />
                  {renderReplyAvatars()}
                </View>
              </View>
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Expanded Replies */}
      {isRepliesExpanded && post.replies.length > 0 && (
        <>
          <CustomSpacing size="m" type="vertical" />
          {renderExpandedReplies()}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  responseCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  authorAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  headerInfo: {
    flex: 1,
  },
  authorRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  newBadge: {
    backgroundColor: "#0084FF",
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 10,
    flexShrink: 0,
  },
  newBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
  },
  newBadgeSmall: {
    backgroundColor: "#0084FF",
    borderRadius: 100,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginRight: 8,
    flexShrink: 0,
  },
  newBadgeTextSmall: {
    color: "#FFFFFF",
    fontSize: 9,
  },
  authorName: {
    color: "#161616",
    flex: 1,
  },
  authorNameWithBadge: {
    flex: 1,
  },
  timestamp: {
    color: "#848484",
    marginTop: 2,
  },
  messageContainer: {
    width: "100%",
  },
  message: {
    color: "#344054",
    lineHeight: 28,
  },
  actionContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  replyEditorContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F4F6",
  },
  replyEditorHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  currentUserAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  replyToText: {
    color: "#161616",
  },
  editorContainer: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    minHeight: 100,
  },
  replyButtonsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  repliesSection: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#F3F4F6",
  },
  repliesRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  repliesCountContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  repliesCount: {
    color: "#161616",
  },
  repliesCountWithBadge: {
    color: "#161616",
  },
  replyAvatarsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  replyAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
  },
  replyAvatarText: {
    color: "#FFFFFF",
    fontSize: 10,
  },
  expandedRepliesScrollContainer: {
    maxHeight: 400,
    marginLeft: 12,
    paddingLeft: 16,
    borderLeftWidth: 2,
    borderLeftColor: "#E5E7EB",
  },
  expandedRepliesContainer: {
    paddingBottom: 8,
  },
  replyCard: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#F3F4F6",
  },
  replyHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  replyInfo: {
    flex: 1,
    marginLeft: 12,
  },
  replyAuthorRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  replyAuthorName: {
    color: "#161616",
    flex: 1,
  },
  replyAuthorNameWithBadge: {
    flex: 1,
  },
  replyTimestamp: {
    color: "#848484",
  },
  replyMessageContainer: {
    marginLeft: 48,
  },
  replyMessage: {
    color: "#374151",
    lineHeight: 20,
  },
});

export default ResponseCard;
